/**
 * Link Topography / Toposolid Tool
 *
 * Links external terrain data (Civil 3D surface exports / ACC point grids)
 * into the project. Accepted formats are textual elevation grids and scan
 * dumps (.csv, .txt, .asc, .xyz, .pts): "x y z" rows. The picked surface is
 * converted into a real Toposolid element — convex-hull boundary + base
 * elevation — so the terrain renders in the viewport and is managed through
 * the shared LinkManager registry (Manage Links).
 */

import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import { parsePointCloudFile, isParsablePointCloud, PointCloudData } from '../services/pointcloud/PointCloudParser';
import { convexHullXY } from '../services/pointcloud/PointCloudParser';
import i18n from '../i18n';

export class LinkTopographyTool extends BaseTool {
    id = 'link_topography';
    name = 'Link Topography / Toposolid';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    onActivate(): void {
        // Dialog-driven command: pick the source surface file first, then
        // create the linked toposolid once the geometry is parsed.
        void this.pickFile('.csv,.txt,.asc,.xyz,.pts').then(async (file) => {
            if (file) {
                if (isParsablePointCloud(file.name)) {
                    await this.linkSurface(file);
                } else {
                    this.notify(i18n.t('tools.notify.surfaceFormatUnsupported', { fileName: file.name }), 'error');
                }
            }
            this.exitToSelect();
        });
    }

    /** Parse an elevation grid and register it as a linked Toposolid. */
    public async linkSurface(file: File): Promise<void> {
        if (!this.context) return;
        try {
            const data: PointCloudData = await parsePointCloudFile(file);
            const pts: [number, number][] = [];
            for (let i = 0; i < data.positions.length; i += 3) {
                pts.push([data.positions[i], data.positions[i + 1]]);
            }
            const boundary = convexHullXY(pts);
            if (boundary.length < 3) {
                this.notify(i18n.t('tools.notify.topoNo2dExtent', { fileName: file.name }), 'warning');
                return;
            }
            let sumZ = 0;
            let minZ = Infinity;
            for (let i = 2; i < data.positions.length; i += 3) {
                sumZ += data.positions[i];
                minZ = Math.min(minZ, data.positions[i]);
            }
            const baseElevation = data.bounds[5] - data.bounds[2] > 0.5 ? minZ : sumZ / data.count;

            const link = this.linkManager.linkFile('Topography', file.name, {
                positioning: 'OriginToOrigin',
                size: file.size,
            });

            const toposolid = {
                id: uuidv4(),
                category: 'Toposolid',
                type: `Linked Toposolid: ${link.name}`,
                parameters: {
                    'File Path': file.name,
                    'Source Points': String(data.count),
                    'Material': 'Grass',
                },
                constraints: [],
                geometryData: {
                    boundary,
                    height: 3.0,
                    baseElevation,
                },
            };
            this.context.bimStore.addElement(toposolid as any);
            this.linkManager.setDocumentId(link.id, toposolid.id);
            this.notify(i18n.t('tools.notify.topographyLinked', { fileName: file.name, count: data.count }), 'success');
        } catch (err) {
            this.notify(i18n.t('tools.notify.surfaceReadFailed', { message: (err as Error).message }), 'error');
        }
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: no interactive placement.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }
}
